import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { MesaVista } from '@shared/types';
import { NumberField } from '../ui/NumberField';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  abierto: boolean;
  mesa: MesaVista | null;
  onClose: () => void;
  onSaved: () => void;
}

export function FormMesa({ abierto, mesa, onClose, onSaved }: Props) {
  const [numero, setNumero] = useState<number>(1);
  const [capacidad, setCapacidad] = useState<number>(4);
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (mesa) {
      setNumero(mesa.numero);
      setCapacidad(mesa.capacidad);
      setObservaciones(mesa.observaciones ?? '');
    } else {
      setNumero(1);
      setCapacidad(4);
      setObservaciones('');
    }
    setError(null);
  }, [mesa, abierto]);

  if (!abierto) return null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (numero < 1 || capacidad < 1) {
      setError('Número y capacidad deben ser mayores a 0');
      return;
    }
    setEnviando(true);
    const data = { numero, capacidad, observaciones: observaciones || undefined };
    const res = mesa
      ? await window.api.mesas.actualizar(mesa.id, data)
      : await window.api.mesas.crear(data);
    setEnviando(false);
    if (res.ok) onSaved();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold">{mesa ? 'Editar mesa' : 'Nueva mesa'}</h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="mesaNum">Número *</label>
              <NumberField
                id="mesaNum"
                min={1}
                decimals={0}
                className="input"
                value={numero}
                onChange={setNumero}
                autoFocus
              />
            </div>
            <div>
              <label className="label" htmlFor="mesaCap">Capacidad *</label>
              <NumberField
                id="mesaCap"
                min={1}
                decimals={0}
                className="input"
                value={capacidad}
                onChange={setCapacidad}
              />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="mesaObs">Observaciones</label>
            <input
              id="mesaObs"
              className="input"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Junto a ventana, terraza..."
              maxLength={200}
            />
          </div>
          {error && <div className="error-text">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost" disabled={enviando}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {mesa ? 'Guardar' : 'Crear mesa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
